// src/components/main/Header.jsx
import React, { useEffect, useRef, useState, useContext, useMemo, useCallback } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { ChannelContext } from '../../contexts/ChannelContext';
import ProgressBar from '../../pages/platform/Channel/modules/headers/ProgressBar.jsx';
import toastr from 'toastr';
import { LogOut } from 'lucide-react';
import { Button } from '../ui/button';

const GlitchText = ({ text, onPress }) => {
  const red = useAnimation();
  const cyan = useAnimation();

  useEffect(() => {
    red.start({ x:[0,-1,2,-2,1,0], y:[0,1,-1,2,-2,0], opacity:[0.22,0.85,0.3,0.9,0.28,0.22], transition:{ duration:0.7, repeat:Infinity, ease:'linear' }});
    cyan.start({ x:[0,2,-1,3,-2,0], y:[0,-2,1,-1,2,0], opacity:[0.22,0.9,0.28,0.85,0.26,0.22], transition:{ duration:0.8, repeat:Infinity, ease:'linear' }});
  }, [red, cyan]);

  const handleClick = (e) => {
    // чтобы клик не всплывал на h1 и не вызывал toggle второй раз
    e.stopPropagation?.();
    onPress?.();
  };

  return (
    <span className="relative inline-block" onClick={handleClick}>
      <span className="relative z-10 cursor-pointer select-none">{text}</span>
      <motion.span aria-hidden className="absolute left-0 top-0" style={{ color:'#ff2a2a', pointerEvents:'none' }} animate={red}>{text}</motion.span>
      <motion.span aria-hidden className="absolute left-0 top-0" style={{ color:'#2affff', pointerEvents:'none' }} animate={cyan}>{text}</motion.span>
    </span>
  );
};

const normId = (p) => String(p || '').trim().toLowerCase().replace(/[^a-z]/g, '');
const labelMap = { twitch:'Twitch', kick:'Kick', wtv:'W.tv' };
const parseDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(+v) ? null : v;
  if (typeof v === 'number') { const ms = v > 1e12 ? v : v * 1000; const d = new Date(ms); return isNaN(+d) ? null : d; }
  const d = new Date(v); return isNaN(+d) ? null : d;
};
function getPlatformEndDate(platformObj) {
  if (!platformObj || platformObj.subscribed !== true) return null;
  let best = null;
  if (Array.isArray(platformObj.items)) {
    for (const it of platformObj.items) {
      const d = parseDate(it?.end_date || it?.end || it?.expires_at);
      if (d && (!best || d > best)) best = d;
    }
  }
  if (platformObj.endDate) {
    const d = parseDate(platformObj.endDate);
    if (d && (!best || d > best)) best = d;
  }
  if (platformObj.byChannel && typeof platformObj.byChannel === 'object') {
    for (const k of Object.keys(platformObj.byChannel)) {
      const d = parseDate(platformObj.byChannel[k]);
      if (d && (!best || d > best)) best = d;
    }
  }
  return best && best.getTime() > Date.now() ? best : null;
}

const Header = ({ subscription, username, userCoins, platform, onLogout }) => {
  const location = useLocation();
  const isChannelPage = useMemo(() => /\/channel\//i.test((location.pathname || '')), [location.pathname]);

  const pid = normId(platform);
  const bgClass = useMemo(() => {
    switch (pid) {
      case 'kick':   return 'bg-green-600';
      case 'twitch': return 'bg-purple-600';
      case 'wtv':    return 'bg-blue-600';
      default:       return 'bg-neutral-900';
    }
  }, [pid]);

  // ==== фоновые эффекты (3D-tilt выключаем на channel/*) ====
  const headerRef = useRef(null);
  const nameRef = useRef(null);
  useEffect(() => {
    if (isChannelPage) { if (nameRef.current) nameRef.current.style.transform = 'none'; return; }
    const handleMouseMove = (e) => {
      const el = headerRef.current; if (!el) return;
      const r = el.getBoundingClientRect();
      const nx = ((e.clientX - r.left) / r.width) * 2 - 1;
      const ny = ((e.clientY - r.top) / r.height) * 2 - 1;
      if (nameRef.current) {
        nameRef.current.style.transform = `rotateX(${ny * 10}deg) rotateY(${-nx * 10}deg)`;
        nameRef.current.style.transformStyle = 'preserve-3d';
      }
    };
    const reset = () => { if (nameRef.current) nameRef.current.style.transform = 'rotateX(0deg) rotateY(0deg)'; };
    const el = headerRef.current;
    el?.addEventListener('mousemove', handleMouseMove);
    el?.addEventListener('mouseleave', reset);
    return () => {
      el?.removeEventListener('mousemove', handleMouseMove);
      el?.removeEventListener('mouseleave', reset);
    };
  }, [isChannelPage]);

  // ==== контекст канала ====
  const { tokensChecked, totalTokens, chatRunning, toggleChat, channelId, groupId, progress, fallbackActive, fallbackTotal } =
    useContext(ChannelContext);

  const [progressEvt, setProgressEvt] = useState(null);
  useEffect(() => {
    const handler = (e) => setProgressEvt(e?.detail || null);
    window.addEventListener('channel:progress', handler);
    return () => window.removeEventListener('channel:progress', handler);
  }, []);
  const barProps = useMemo(() => {
    const src = progressEvt || {};
    return {
      platform: src.platform ?? platform,
      progress: src.progress ?? progress,
      tokensChecked: src.tokensChecked ?? tokensChecked,
      totalTokens: src.totalTokens ?? totalTokens,
      fallbackActive: src.fallbackActive ?? fallbackActive,
      fallbackTotal: src.fallbackTotal ?? fallbackTotal,
    };
  }, [progressEvt, platform, progress, tokensChecked, totalTokens, fallbackActive, fallbackTotal]);

  // ==== Подписка: только для активной на ТЕКУЩЕЙ платформе ====
  const subscriptionLine = useMemo(() => {
    const platformObj = pid ? subscription?.[pid] : null;
    const end = getPlatformEndDate(platformObj);
    if (!end) return null;
    const pretty = end.toLocaleDateString('ru-RU', { day:'2-digit', month:'2-digit', year:'numeric' });
    const label  = labelMap[pid] || (pid?.toUpperCase?.() || '');
    return `Подписка на ${label} до ${pretty}`;
  }, [subscription, pid]);

  // ==== Музыка: DOM <audio> + WebAudio фейды ====
  const audioElRef = useRef(null);
  const audioCtxRef = useRef(null);
  const gainRef = useRef(null);
  const isFadingRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioSrc = (process.env.PUBLIC_URL || '') + '/ses.mp3';

  const ensureAudioEl = useCallback(() => {
    if (audioElRef.current && document.body.contains(audioElRef.current)) return audioElRef.current;
    let el = document.getElementById('ses-audio');
    if (!el) {
      el = document.createElement('audio');
      el.id = 'ses-audio';
      el.preload = 'auto';
      el.style.display = 'none';
      document.body.appendChild(el);
    }
    el.src = audioSrc;
    el.crossOrigin = 'anonymous';
    try { el.volume = 1; el.muted = false; } catch {}
    el.onplay  = () => setIsPlaying(true);
    el.onpause = () => setIsPlaying(false);
    el.onended = () => setIsPlaying(false);
    audioElRef.current = el;
    try { el.load(); } catch {}
    return el;
  }, [audioSrc]);

  const ensureAudioGraph = useCallback((el) => {
    if (!el) return;
    if (audioCtxRef.current && gainRef.current) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const src = ctx.createMediaElementSource(el);
    const gain = ctx.createGain();
    gain.gain.value = 0;
    src.connect(gain).connect(ctx.destination);
    audioCtxRef.current = ctx;
    gainRef.current = gain;
  }, []);

  const fadeTo = useCallback(async (target = 1, ms = 600) => {
    const el = audioElRef.current;
    if (!el) return;
    isFadingRef.current = true;
    try {
      const ctx = audioCtxRef.current;
      const gain = gainRef.current;
      if (ctx && gain) {
        const now = ctx.currentTime;
        const start = gain.gain.value;
        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(start, now);
        gain.gain.linearRampToValueAtTime(target, now + ms / 1000);
        await new Promise((r) => setTimeout(r, ms));
      } else {
        const start = el.volume ?? 1;
        const steps = Math.max(6, Math.floor(ms / 40));
        const delta = (target - start) / steps;
        let i = 0;
        await new Promise((resolve) => {
          const id = setInterval(() => {
            i += 1;
            const v = Math.max(0, Math.min(1, start + delta * i));
            try { el.volume = v; } catch {}
            if (i >= steps) { clearInterval(id); resolve(); }
          }, Math.max(16, Math.floor(ms / 60)));
        });
      }
    } finally {
      isFadingRef.current = false;
    }
  }, []);

  const waitCanPlay = (el, ms = 2500) => new Promise((resolve) => {
    if (!el) return resolve();
    if (el.readyState >= 3) return resolve();
    const on = () => { el.removeEventListener('canplaythrough', on); resolve(); };
    el.addEventListener('canplaythrough', on, { once: true });
    setTimeout(() => { el.removeEventListener('canplaythrough', on); resolve(); }, ms);
  });

  const toggleMusic = useCallback(async () => {
    if (isFadingRef.current) return;
    const el = ensureAudioEl();
    ensureAudioGraph(el);
    try {
      await waitCanPlay(el);
      if (el.paused || el.ended) {
        if (gainRef.current) gainRef.current.gain.value = 0;
        else try { el.volume = 0; } catch {}
        try {
          const p = el.play();
          if (p && p.catch) await p.catch(async (e) => {
            el.muted = true;
            const p2 = el.play();
            if (p2 && p2.catch) await p2.catch(() => { throw e; });
            setTimeout(() => { el.muted = false; }, 50);
          });
          await fadeTo(1, 600); // ▶︎ плавный старт
        } catch (e) {
          console.error('Audio play failed:', e);
          toastr.error('Не удалось воспроизвести аудио. Проверьте /public/ses.mp3');
        }
      } else {
        await fadeTo(0, 600); // ⏸︎ плавное затухание
        el.pause();
        if (gainRef.current) gainRef.current.gain.value = 1;
        else try { el.volume = 1; } catch {}
      }
    } catch (e) {
      console.error('Audio toggle error:', e);
      toastr.error('Ошибка аудио (детали в консоли).');
    }
  }, [ensureAudioEl, ensureAudioGraph, fadeTo]);

  const handleToggleChat = () => {
    if (!channelId || !groupId) { toastr.error('Не заданы channelId или groupId', 'Ошибка'); return; }
    toggleChat();
  };
  const runVariant = chatRunning ? 'destructive' : 'primary';

  return (
    <motion.div
      ref={headerRef}
      className={`fixed top-0 left-0 right-0 z-[58] ${bgClass} text-white h-[72px]`}
      initial={{ opacity: 0, y: -50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      style={{ perspective: '1000px' }}
    >
      {!isChannelPage && (
        <>
          <motion.div
            className="pointer-events-none absolute -top-6 -left-10 w-[32%] h-[200%] blur-3xl opacity-35 mix-blend-screen"
            animate={{ x:[0,16,-8,0], y:[0,-6,6,0], scale:[1,1.04,0.98,1] }}
            transition={{ duration:16, repeat:Infinity, ease:'easeInOut' }}
            style={{ background: 'radial-gradient(60% 60% at 50% 50%, #00ffd5 0%, transparent 70%)' }}
          />
          <motion.div
            className="pointer-events-none absolute -bottom-10 -right-10 w-[38%] h-[200%] blur-3xl opacity-30 mix-blend-screen"
            animate={{ x:[0,-12,8,0], y:[0,6,-6,0], scale:[1,0.98,1.03,1] }}
            transition={{ duration:18, repeat:Infinity, ease:'easeInOut' }}
            style={{ background: 'radial-gradient(60% 60% at 50% 50%, #7c3cff 0%, transparent 70%)' }}
          />
          <div className="pointer-events-none absolute inset-0 opacity-[0.08]"
               style={{ background: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.2) 0px, rgba(255,255,255,0.2) 1px, transparent 2px, transparent 4px)' }} />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-black/10" />
        </>
      )}

      {/* НИК + (возможная) подписка */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[60]">
        <div className="flex items-center gap-3 md:gap-4">
          <div className="flex flex-col items-center gap-0.5">
            <h1
              id="channel-username"
              ref={nameRef}
              className="text-lg sm:text-xl font-extrabold tracking-wide will-change-transform select-none cursor-pointer"
              // ВАЖНО: onClick вешаем ТОЛЬКО на plain-ник (страницы channel/*)
              onClick={isChannelPage ? toggleMusic : undefined}
              title={isPlaying ? 'Остановить' : 'Воспроизвести'}
              style={isChannelPage ? { filter: 'none', textShadow: 'none', transform: 'none' } : undefined}
            >
              {isChannelPage
                ? username
                : <GlitchText text={username} onPress={toggleMusic} />
              }
            </h1>

            {subscriptionLine && (
              <span id="subscription-end-date" className="text-[10px] sm:text-xs opacity-90">
                {subscriptionLine}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Правая часть */}
      <div className="absolute inset-y-0 right-3 flex items-center gap-3">
        {isChannelPage ? (
          <>
            <ProgressBar
              platform={barProps.platform}
              progress={barProps.progress}
              tokensChecked={chatRunning ? barProps.tokensChecked : 0}
              totalTokens={barProps.totalTokens}
              fallbackActive={chatRunning ? barProps.fallbackActive : 0}
              fallbackTotal={chatRunning ? barProps.fallbackTotal : 0}
              className="origin-right"
              numbersClass="text-white"
            />
            <Button
              variant={runVariant}
              size="sm"
              className="transition-colors duration-300"
              onClick={handleToggleChat}
              disabled={!channelId || !groupId}
            >
              {chatRunning ? 'Остановить' : 'Запустить'}
            </Button>
          </>
        ) : (
          <>
            <div className="relative flex h-9 items-center gap-2 bg-white/10 backdrop-blur-sm pl-2.5 pr-2 rounded-lg ring-1 ring-white/15 shadow">
              <div className="relative grid place-items-center" style={{ width: 32, height: 32 }}>
                <div className="w-[24px] h-[24px] rounded-full grid place-items-center shadow-inner"
                     style={{ background: 'radial-gradient(circle at 50% 35%, #fff59d, #fbc02d 60%, #f57f17 100%)' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
                    <circle cx="12" cy="12" r="7" fill="rgba(0,0,0,.15)" />
                    <path d="M9 12h6" stroke="rgba(255,255,255,.65)" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="absolute inset-[2px] rounded-full ring-1 ring-white/15 pointer-events-none" />
              </div>
              <h3 className="text-sm">
                <span id="coins-count" className="font-semibold">{userCoins}</span> S-Coin
              </h3>
            </div>

            {onLogout && (
              <button
                type="button"
                aria-label="Logout"
                onClick={onLogout}
                className="inline-flex h-9 items-center gap-2 rounded-lg px-3 font-semibold text:white
                           bg-white/10 backdrop-blur-sm ring-1 ring-white/15
                           shadow-[0_10px_30px_rgba(0,0,0,.25)]
                           hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/70
                           transition"
              >
                <LogOut className="h-5 w-5" aria-hidden />
                <span className="hidden sm:inline">Logout</span>
              </button>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
};

export default Header;
